// PhoneVerifyRewardHandler.jsx
import { useEffect } from 'react';
import PropTypes from 'prop-types';

import defaults from '../resource/defaults.js';
import { getEncodedTemplateId } from '../resource/oembedUtils.js';
import { encodeUnicode } from '../resource/base64Utils.js';

const ONE_DAY_MSEC = 1000 * 60 * 60 * 24;

const PHONE_VERIFY_REWARD_NOTIFICATION_TIMESTAMP_KEY =
  '__phoneVerifyRewardNotificationTimestamp';

export const PhoneVerifyRewardHandler = ({
  token = '',
  oembedReferralPendingRedemptionTemplateId = '',
  phoneVerifyRewardNotificationCooldownMsec = ONE_DAY_MSEC,
  isPhoneBound = false,
  isFromRegisterEntry = true,
  isPendingReferralRedemption = false,
  isMeAccountLinksFetched = false,
  isMeAccountLinksFetching = false,
  fetchAccountLinks = defaults.NULL_FUNCTION,
  fetchReferralRedeemStatus = defaults.NULL_FUNCTION,
  addPopupNotification = defaults.NULL_FUNCTION,
  addNotificationCenterItem = defaults.NULL_FUNCTION,
}) => {
  useEffect(() => {
    if (token && !isMeAccountLinksFetched && !isMeAccountLinksFetching) {
      fetchAccountLinks();
    }
  }, [
    token,
    isMeAccountLinksFetched,
    isMeAccountLinksFetching,
    fetchAccountLinks,
  ]);

  useEffect(() => {
    const shouldFetchReferralRedeemStatus = token && !isFromRegisterEntry;

    if (shouldFetchReferralRedeemStatus) {
      fetchReferralRedeemStatus();
    }
  }, [token, isFromRegisterEntry, fetchReferralRedeemStatus]);

  useEffect(() => {
    let lastShowPhoneVerifyRewardNotificationTimestamp = 0;

    try {
      lastShowPhoneVerifyRewardNotificationTimestamp = localStorage.getItem(
        PHONE_VERIFY_REWARD_NOTIFICATION_TIMESTAMP_KEY
      );
    } catch (error) {
      lastShowPhoneVerifyRewardNotificationTimestamp = 0;
    }

    const now = Date.now();

    const shouldAddPopupNotification =
      now - lastShowPhoneVerifyRewardNotificationTimestamp >
        phoneVerifyRewardNotificationCooldownMsec &&
      !isPhoneBound &&
      isPendingReferralRedemption &&
      isMeAccountLinksFetched;

    const shouldAddNotificationCenterItem =
      !isPhoneBound && isPendingReferralRedemption && isMeAccountLinksFetched;

    // check if add referral code but not redeem and nor bind phone,
    // once a day for add popup notification
    if (shouldAddPopupNotification) {
      localStorage.setItem(PHONE_VERIFY_REWARD_NOTIFICATION_TIMESTAMP_KEY, now);

      addPopupNotification({
        id: 'phoneVerifyRewardNotification',
        position: 'top',
        link: `?campaign-id=open-modal&campaign=${encodeUnicode({
          str: JSON.stringify({
            modalId: 'PhoneVerifyRewardModal',
          }),
        })}&t=${getEncodedTemplateId({ templateId: oembedReferralPendingRedemptionTemplateId })}`,
        tag: 'phoneVerifyRewardNotification',
        isNotificationCenter: false,
      });
    }

    if (shouldAddNotificationCenterItem) {
      addNotificationCenterItem({
        phoneVerifyRewardNotification: {
          id: 'phoneVerifyRewardNotification',
          timestamp: Date.now() / 1000,
          event: 'notification',
          data: {
            id: 'phoneVerifyRewardNotification',
            link: `/manage-profile/account-linking?&t=${getEncodedTemplateId({ templateId: oembedReferralPendingRedemptionTemplateId })}`,
            position: 'top',
            tag: 'phoneVerifyRewardNotification',
            exp: null,
          },
          read: true,
        },
      });
    }
  }, [
    phoneVerifyRewardNotificationCooldownMsec,
    addPopupNotification,
    addNotificationCenterItem,
    isPhoneBound,
    isPendingReferralRedemption,
    isMeAccountLinksFetched,
    oembedReferralPendingRedemptionTemplateId,
  ]);

  return null;
};

PhoneVerifyRewardHandler.propTypes = {
  token: PropTypes.string,
  oembedReferralPendingRedemptionTemplateId: PropTypes.string,
  phoneVerifyRewardNotificationCooldownMsec: PropTypes.number,
  isPhoneBound: PropTypes.bool,
  isFromRegisterEntry: PropTypes.bool,
  isPendingReferralRedemption: PropTypes.bool,
  isMeAccountLinksFetched: PropTypes.bool,
  isMeAccountLinksFetching: PropTypes.bool,
  fetchAccountLinks: PropTypes.func,
  fetchReferralRedeemStatus: PropTypes.func,
  addPopupNotification: PropTypes.func,
  addNotificationCenterItem: PropTypes.func,
};

export default PhoneVerifyRewardHandler;
